import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
// v1 date range picker, by design choice.
import {
  colors2,
  DateRangePicker,
  LoaderOverlay,
  UI_CAPTION_SEMIBOLD,
} from "oolib";
import {
  ButtonPrimary,
  ButtonTertiary,
} from "oolib/dist/v2/components/Buttons";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { useQueryClient } from "react-query";
import {
  externalConnectionKey,
  useExternalConnectionStatus,
} from "@/utils/react-query-hooks/externalConnections";
import { IntegrationDisconnectConfirmModal } from "../IntegrationDisconnectConfirmModal";
import { IntegrationModalShell } from "../comps/IntegrationModalShell";
import { ConnectionCard } from "../comps/ConnectionCard";
import { EditImportSettings } from "../comps/EditImportSettings";
import { ManageIntegrationModal } from "../comps/ManageIntegrationModal";
import { caption, isoDaysAgo } from "../comps/common";

/**
 * Intercom's popups, on IntegrationModalShell.
 *
 * ImportModal is "Import from Intercom": the connected workspace, a date range
 * and a conversation state. It is the import wizard's Intercom step
 * (DirectDataImport renders it for a connected Intercom). Continue runs
 * Intercom's batch job (initiate, then poll its progress); once it completes
 * the job id goes to onContinue, and the wizard fetches the result and moves
 * on to field mapping, passing back isLoading / error.
 *
 * ManageModal is the shared manage popup, with a range and state to import
 * more. Intercom has no scheduled sync.
 */

const STATE_OPTIONS = [
  { display: "All", value: "all" },
  { display: "Open", value: "open" },
  { display: "Closed", value: "closed" },
  { display: "Snoozed", value: "snoozed" },
];

const BATCH_STATUS_LABELS = {
  initiating: "Starting fetch job...",
  Pending: "Waiting in queue...",
  Running: "Fetching conversations...",
  completing: "Preparing preview...",
};

const POLL_INTERVAL_MS = 3000;
const DAY_MS = 24 * 60 * 60 * 1000;
const SOFT_WARN_DAYS = 90;
const HARD_CAP_DAYS = 365;

// Same default as the old Intercom step: the last 30 days, every state.
const initialRange = () => [isoDaysAgo(30), isoDaysAgo(0)];

// Why a range can't be imported (error), or a size warning, as the old
// Intercom step had them.
const checkRange = ([startDate, endDate]) => {
  if (!startDate || !endDate) return { error: "Pick a start and end date." };
  if (new Date(startDate) > new Date(endDate))
    return { error: "End date must be on or after start date." };
  const days = Math.round((new Date(endDate) - new Date(startDate)) / DAY_MS);
  if (days > HARD_CAP_DAYS)
    return {
      error: `Please select a range of ${HARD_CAP_DAYS} days or fewer. Run multiple imports for longer history.`,
    };
  if (days > SOFT_WARN_DAYS)
    return {
      warn: `Large date ranges (${days} days) may take several minutes to import.`,
    };
  return {};
};

// The picks, as the batch job and the import-more flow take them.
const toOptions = ([startDate, endDate], state) => ({
  startDate,
  endDate,
  state: state?.value || "all",
});

// Intercom's batch fetch: initiate a job, poll it every few seconds, and hand
// the job id to onDone once it completes with conversations. A failed job
// refetches the connection status (a 401 marks it stale server-side, which
// hands the wizard back to its reconnect step).
const useIntercomBatch = (onDone) => {
  const qc = useQueryClient();
  const refreshStatus = () =>
    qc.invalidateQueries(externalConnectionKey("intercom"));
  // null | "running" | "error" | "empty"
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState("");
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const stopPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = null;
  };
  useEffect(() => stopPolling, []);

  const fail = (msg) => {
    stopPolling();
    setPhase("error");
    setError(msg);
  };

  const poll = (jobId, options) => async () => {
    try {
      const { data } = await axios.post("/api/externalImport/batch/status", {
        source: { id: "intercom" },
        jobId,
      });
      setProgress(data.progress || 0);
      const detail = data.fetched
        ? ` (Fetched ${data.fetched}${data.total ? ` of ~${data.total}` : ""} conversations)`
        : "";
      setLabel(`${BATCH_STATUS_LABELS[data.status] || data.status}${detail}`);
      if (data.status === "Completed") {
        stopPolling();
        // Nothing in the range is a notice, not a failure.
        if (data.fetched === 0) return setPhase("empty");
        setPhase(null);
        onDone({ batchJobId: jobId, ...options });
      } else if (data.status === "Failed") {
        fail(
          data.errorMessage || "Intercom fetch job failed. Please try again.",
        );
        refreshStatus();
      }
    } catch (err) {
      fail(
        err.response?.data?.error ||
          err.message ||
          "Failed to check job status",
      );
    }
  };

  const start = async (options) => {
    setPhase("running");
    setProgress(0);
    setLabel(BATCH_STATUS_LABELS.initiating);
    setError(null);
    try {
      const { data } = await axios.post("/api/externalImport/batch/initiate", {
        source: { id: "intercom", options },
      });
      if (!data.batchJobId)
        throw new Error("No batchJobId returned from server");
      const tick = poll(data.batchJobId, options);
      tick();
      pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Failed to start Intercom fetch job";
      fail(msg);
      // No or stale connection server-side: let the wizard catch up.
      if (/reconnect|connection/i.test(msg)) refreshStatus();
    }
  };

  const reset = () => {
    stopPolling();
    setPhase(null);
    setProgress(0);
    setLabel("");
    setError(null);
  };

  return { phase, progress, label, error, start, reset };
};

// Date range and conversation state, shared by the import popup and "Edit
// import settings". inCard: inside a card, so the dropdown pops out of its
// clipping.
const IntercomFields = ({
  dateRange,
  setDateRange,
  convState,
  setConvState,
  readOnly,
  inCard,
}) => {
  const { error, warn } = checkRange(dateRange);
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <DateRangePicker
          id="dateRange"
          label="Date range"
          value={dateRange}
          onChange={(_k, v) => setDateRange(v)}
          readOnly={readOnly}
        />
        {error && (
          <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
            {error}
          </UI_CAPTION_SEMIBOLD>
        )}
        {warn && caption(warn)}
      </div>
      <DropdownSingle
        id="conversationState"
        label="Conversation state"
        placeholder="Select state..."
        options={STATE_OPTIONS}
        value={convState}
        onChange={(_k, v) => setConvState(v)}
        readOnly={readOnly}
        popOutOfOverflowHiddenParent={inCard}
      />
    </>
  );
};

function ImportModal({
  Icon,
  onClose,
  onBack,
  onContinue,
  isLoading,
  error,
  hideBackdrop,
  fromManage,
  initialOptions,
}) {
  const { data: conn } = useExternalConnectionStatus("intercom");
  // initialOptions: picks made elsewhere ("Edit import settings"), run on open
  // and kept here so a failed run can be adjusted and retried.
  const [dateRange, setDateRange] = useState(() =>
    initialOptions
      ? [initialOptions.startDate, initialOptions.endDate]
      : initialRange(),
  );
  const [convState, setConvState] = useState(
    () =>
      STATE_OPTIONS.find((o) => o.value === initialOptions?.state) ||
      STATE_OPTIONS[0],
  );
  const batch = useIntercomBatch(onContinue);
  useEffect(() => {
    if (initialOptions) batch.start(toOptions(dateRange, convState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showDisconnect, setShowDisconnect] = useState(false);

  const busy = batch.phase === "running" || isLoading;
  const canContinue = !checkRange(dateRange).error;
  const [startDate, endDate] = dateRange;

  return (
    <IntegrationModalShell
      title="Import from Intercom"
      Icon={Icon}
      onBack={onBack}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      footer={
        <>
          <ButtonTertiary onClick={onClose}>
            {fromManage ? "Discard" : "Do Later"}
          </ButtonTertiary>
          {batch.phase === "error" || batch.phase === "empty" ? (
            <ButtonPrimary onClick={batch.reset}>Try Again</ButtonPrimary>
          ) : (
            <ButtonPrimary
              onClick={() => batch.start(toOptions(dateRange, convState))}
              disabled={!canContinue || busy}
              isLoading={busy}
            >
              Continue
            </ButtonPrimary>
          )}
        </>
      }
    >
      {busy && (
        <LoaderOverlay
          loaderText={
            batch.phase === "running"
              ? `${batch.progress}% · ${batch.label}`
              : "Fetching data from external source..."
          }
        />
      )}
      {/* Left out when launched from the manage popup, which shows it. */}
      {!fromManage && (
        <ConnectionCard
          account={conn?.providerAccountName || "Intercom"}
          email={conn?.adminEmail}
          disconnectDisabled={busy}
          onDisconnect={() => setShowDisconnect(true)}
        />
      )}
      {showDisconnect && (
        <IntegrationDisconnectConfirmModal
          provider="intercom"
          name="Intercom"
          onClose={() => setShowDisconnect(false)}
          // Nothing to import from once disconnected.
          onDisconnected={onClose}
        />
      )}
      {caption(
        "Import Intercom conversations into your dataset. Each conversation becomes a row, with its full thread (including internal notes) as a transcript.",
      )}
      <IntercomFields
        dateRange={dateRange}
        setDateRange={setDateRange}
        convState={convState}
        setConvState={setConvState}
        readOnly={busy}
      />
      {batch.phase === "empty" &&
        caption(
          `No conversations found in Intercom for ${startDate} → ${endDate}${
            convState?.value !== "all" ? ` (state: ${convState.value})` : ""
          }. Try changing the date range.`,
        )}
      {[batch.phase === "error" && batch.error, error]
        .filter(Boolean)
        .map((msg) => (
          <UI_CAPTION_SEMIBOLD key={msg} style={{ color: colors2.red }}>
            {msg}
          </UI_CAPTION_SEMIBOLD>
        ))}
    </IntegrationModalShell>
  );
}

// "Edit import settings": pick a range and state to import more conversations
// into this dataset. Save opens the import wizard, which runs them straight
// away, maps fields and appends.
const EditIntercomImport = ({ onSave }) => {
  const [dateRange, setDateRange] = useState(initialRange);
  const [convState, setConvState] = useState(STATE_OPTIONS[0]);
  return (
    <EditImportSettings
      saveDisabled={!!checkRange(dateRange).error}
      onSave={() => onSave(toOptions(dateRange, convState))}
    >
      <IntercomFields
        dateRange={dateRange}
        setDateRange={setDateRange}
        convState={convState}
        setConvState={setConvState}
        inCard
      />
    </EditImportSettings>
  );
};

// The shared manage popup, with Intercom's "Edit import settings".
const ManageModal = (props) => (
  <ManageIntegrationModal
    provider="intercom"
    EditImport={EditIntercomImport}
    {...props}
  />
);

export const intercom = {
  ImportModal,
  ManageModal,
  // The popup runs the batch job itself, so picks made elsewhere go to it
  // rather than straight to the wizard's fetch.
  runsOwnFetch: true,
};
